import React, { useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import { Task } from '../../../../../models/MeetingSupport'
import { User } from '../../../../../models/User'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { selectTaskPollingEnabled } from '../../../../../store/task/task'
import { addToast } from '../../../../../store/toast/toastSlice'
import { NewFormTask, UserGroupTasks } from '../../../../../views/Tasks/Tasks'
import { useTaskSelectionHandler } from '../../common/hooks/useTaskSelectionHandler'
import { useGetClientsPreviousMeetingTasksQuery } from '../../store/fa'

import './Tasks.scss'

const EmptyState: React.FC = () => {
  return (
    <div className='bg-white border rounded-3 p-3'>
      <div className='d-flex align-items-center justify-content-center'>
        <h5 className='mb-0'>All caught up! No open tasks with these participants.</h5>
      </div>
    </div>
  )
}

interface TaskListProps {
  groupedTasks: { tasks: Task[]; user: User }[]
  isLoading: boolean
}

const TaskList: React.FC<TaskListProps> = ({ groupedTasks, isLoading }) => {
  const roomSession = useAppSelector(selectRoomSession)
  const isPolling = useAppSelector(selectTaskPollingEnabled)
  const dispatch = useAppDispatch()

  const { selectionHandlers } = useTaskSelectionHandler()

  const contactsForMeeting = useMemo(() => roomSession?.users.filter((e) => e.type === 'external') ?? [], [roomSession?.users])

  if (!roomSession) {
    return null
  }
  const { roomId, id: sessionId, roomCreatedBy, users } = roomSession

  const initialRelatedToUser = contactsForMeeting.length === 1 ? contactsForMeeting[0] : null

  const userIds = new Set([...users.map((user) => user.id), ...(roomCreatedBy ? [roomCreatedBy.id] : [])])

  const groupedTasksForMeetingParticipants = groupedTasks.filter(({ user }) => userIds.has(user.id))

  const formTaskToCreateTaskPayload = (assignedToUserId: string, formTask: NewFormTask) => {
    const { subject, description, relatedToUserId } = formTask
    return {
      ...(relatedToUserId ? { relatedTo: { type: 'user', id: relatedToUserId } } : {}),
      ...(description ? { description } : {}),
      assignedTo: { type: 'user', id: assignedToUserId },
      task: subject,
      type: 'meeting',
      roomId,
      sessionId,
    }
  }

  const handleCopyTasks = () => {
    const tasksString = groupedTasksForMeetingParticipants
      .map(({ user, tasks }) => {
        return `${user.name}: \n${tasks
          .map((task) => {
            const { task: subject, description } = task
            const descriptionPart = description ? `: ${description}` : ''
            return `\t- ${subject}${descriptionPart}`
          })
          .join('\n')}\n`
      })
      .join('')

    navigator.clipboard
      .writeText(tasksString)
      .then(() => {
        dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Tasks copied to the clipboard'] }))
      })
      .catch((err) => {
        console.error('Failed to copy tasks to clipboard:', err)
        dispatch(addToast({ variant: 'error', title: 'Copy failed', messages: ['Failed to copy tasks to clipboard'] }))
      })
  }

  if (isLoading || isPolling) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (groupedTasksForMeetingParticipants.length === 0) {
    return <EmptyState />
  }

  return (
    <div className='bg-white border rounded-3 p-3 pb-4'>
      <div className='d-flex align-items-center justify-content-between mb-3 ps-3'>
        <h5 className='mb-0'>Open tasks from previous meeting</h5>
        <button className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 paragraph-small bold text-dark' onClick={handleCopyTasks}>
          <i className='bi bi-files' />
          Copy all
        </button>
      </div>
      <div className='d-flex flex-column gap-2'>
        {groupedTasksForMeetingParticipants.map(({ user, tasks }, index) => (
          <div key={user.id} className='bg-white border rounded-3 p-3 pb-4'>
            <UserGroupTasks
              user={user}
              tasks={tasks}
              row={index}
              formTaskToCreateTaskPayload={formTaskToCreateTaskPayload}
              defaultRelatedToOptions={users}
              initialRelatedToUser={initialRelatedToUser}
              selectionHandlers={selectionHandlers}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

interface TasksProps {
  accountId: string
  clients: User[]
}

export function Tasks({ accountId, clients }: TasksProps) {
  const { data: groupedTasks = [], isLoading } = useGetClientsPreviousMeetingTasksQuery({ accountId, clients }, { skip: clients.length === 0 })
  return <TaskList groupedTasks={groupedTasks} isLoading={isLoading} />
}
